/**
 * Step 3: writing the body, one section at a time.
 *
 * It used to be one call for the whole article, and that had two faults no
 * wording fixes. The length landed wherever it landed - the measured run came
 * back at 57% of what it asked for - and every H2 was written without knowing
 * what the others would cover, so sections repeated each other's openings and
 * each other's explanations.
 *
 * Now each section is its own call, and the loop runs HERE rather than on the
 * server. That is not an implementation detail:
 *
 *   a writer watching six sections complete one by one is being told what is
 *   happening, where a single 60-second request is a spinner;
 *
 *   a section that comes out weak is redrawn on its own, for the cost of one
 *   section and without touching the others;
 *
 *   and a failure half way through leaves five good sections in place rather
 *   than nothing.
 *
 * Nothing is written into the article until 本文として保存 - the promise this
 * screen has always made. Drafts live on the sections until then.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLoaderData, useLocation, useNavigate } from 'react-router';
import {
  ApiError,
  patch,
  post,
  type ArticleDetail,
  type ArticleSection,
  type GenerationContext,
  type RewriteResult,
} from '../api.ts';
import { Card, Empty, Pill } from '../components/ui.tsx';
import { StageBar } from '../components/StageBar.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { RichEditor } from '../components/RichEditor.tsx';
import { RewritePopover } from '../components/RewritePopover.tsx';
import { toHtml } from '../rich-text.ts';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { visibleLength } from '../../../shared/rich-format.ts';

interface LoaderData {
  article: ArticleDetail;
  context: GenerationContext;
}

interface DraftResult {
  index: number;
  heading: string;
  text: string;
  model: string;
  provider: string;
  microCost: number;
}

/** How far off target a section may be before the screen says so. */
const LENGTH_TOLERANCE = 0.3;

/** How long to wait after the last keystroke before saving a section. */
const SAVE_AFTER_MS = 1200;

/**
 * One section, editable where it is read.
 *
 * Saved on its own rather than with the article: the article has no body yet -
 * that is what 本文として保存 makes - so an edit here has nowhere to go except
 * onto the section row. Which means a reload keeps it, and assembling later
 * carries it into the article.
 *
 * Saved on a pause rather than on a button, because a button somebody does not
 * press is the same as no saving at all. The state is shown, so a writer never
 * has to guess whether their fix survived.
 */
function SectionEditor({
  articleId,
  ordinal,
  value,
  onChange,
}: {
  articleId: string;
  ordinal: number;
  value: string;
  onChange: (bodyMd: string) => void;
}) {
  /*
   * The same selection rewrite the finishing screen has.
   *
   * A section is where a weak paragraph is actually noticed - it is being read
   * for the first time, next to the target it missed - and until now the only
   * answer here was 書き直す, which regenerates the whole section and throws away
   * everything in it that was fine. The finishing screen has had the passage-level
   * version for a while; it belongs at the point the problem is found.
   *
   * Per section, not per screen: eight editors, and the panel has to act on the
   * one whose text is selected. Keeping the state inside this component is what
   * makes that true without any of them knowing about the others.
   */
  const editorRef = useRef<TiptapEditor | null>(null);
  const [selected, setSelected] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [style, setStyle] = useState('');
  const [note, setNote] = useState('');
  const [rewriting, setRewriting] = useState(false);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const [rewritten, setRewritten] = useState<RewriteResult | null>(null);
  const [state, setState] = useState<'clean' | 'pending' | 'saving' | 'failed'>('clean');
  const [text, setText] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /*
   * What this component last emitted upward.
   *
   * Typing is handed to the parent so the character counts stay honest, and it
   * comes straight back as `value`. Without remembering what we sent, the effect
   * below would treat our own keystroke as a redraw arriving from outside and
   * reset the editor mid-sentence.
   */
  const emitted = useRef(value);

  /* A redraw replaces the section: that IS an outside change, and the server has
     already stored it, so there is nothing to save. */
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setText(value);
    setState('clean');
    if (timer.current) clearTimeout(timer.current);
  }, [value]);

  // Nothing keeps ticking after the card is gone.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  function edited(bodyMd: string) {
    emitted.current = bodyMd;
    setText(bodyMd);
    onChange(bodyMd);
    setState('pending');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setState('saving');
      patch(`/articles/${articleId}/sections/${ordinal}`, { bodyMd })
        .then(() => setState('clean'))
        .catch(() => setState('failed'));
    }, SAVE_AFTER_MS);
  }

  /**
   * What surrounds the passage, so the replacement fits where it lands.
   *
   * Taken from this SECTION's text rather than the whole article: that is what
   * this editor holds, and it is the 600 characters either side that actually
   * decide whether a rewritten paragraph repeats the one above it.
   */
  function neighbours(passage: string): { before?: string; after?: string } {
    const at = text.indexOf(passage.trim());
    if (at === -1) return {};
    const end = at + passage.trim().length;
    return { before: text.slice(Math.max(0, at - 600), at), after: text.slice(end, end + 600) };
  }

  async function runRewrite() {
    if (selected.trim() === '') return;
    setRewriting(true);
    setRewriteError(null);
    setRewritten(null);
    try {
      setRewritten(
        await post<RewriteResult>(`/articles/${articleId}/rewrite`, {
          selection: selected,
          ...neighbours(selected),
          ...(style ? { style } : {}),
          ...(note.trim() ? { instruction: note.trim() } : {}),
        }),
      );
    } catch (err) {
      /*
       * Said in the panel, not in the card's error strip. The popover sits over
       * the passage the writer is looking at; a refusal reported anywhere else
       * reads as the button having done nothing.
       */
      setRewriteError(
        err instanceof ApiError
          ? err.status === 409
            ? 'リライトにはペルソナが必要です。「記事の条件」で選んでください。'
            : err.code === 'quota_exceeded'
              ? '今月の生成上限に達しています。'
              : (err.fields[0] ?? err.message)
          : '書き直せませんでした',
      );
    } finally {
      setRewriting(false);
    }
  }

  /*
   * Replaced in the document, which then saves itself: `insertContent` fires the
   * editor's own update, which is the same path a keystroke takes - so the
   * rewritten passage is persisted by the pause-save like anything else typed.
   */
  function applyRewrite() {
    if (!rewritten) return;
    editorRef.current?.chain().focus().insertContent(toHtml(rewritten.text)).run();
    setRewritten(null);
    setRect(null);
  }

  return (
    <>
      <RichEditor
        value={text}
        onChange={edited}
        compact
        editorRef={editorRef}
        onSelectionChange={(passage, where) => {
          setSelected(passage);
          setRect(where);
          if (passage.trim() === '') setRewritten(null);
        }}
      />
      <RewritePopover
        selected={selected}
        anchor={rect}
        style={style}
        onStyle={setStyle}
        note={note}
        onNote={setNote}
        busy={rewriting}
        error={rewriteError}
        result={rewritten}
        onRun={() => void runRewrite()}
        onApply={applyRewrite}
        onDiscard={() => setRewritten(null)}
        onClose={() => {
          setRect(null);
          setRewritten(null);
          setRewriteError(null);
        }}
      />
      {state !== 'clean' && (
        <p className="muted small" style={{ margin: '6px 0 0' }} role="status">
          {state === 'saving'
            ? '保存中…'
            : state === 'failed'
              ? '保存できませんでした。もう一度編集すると再試行します。'
              : '未保存の変更があります'}
        </p>
      )}
    </>
  );
}

export default function ArticleWrite() {
  const { article, context } = useLoaderData<LoaderData>();
  const navigate = useNavigate();
  const location = useLocation();

  /** The outline, with whatever each section already holds. */
  const [sections, setSections] = useState<ArticleSection[]>(article.sections);
  /** Which section is being written right now, by ordinal. */
  const [writing, setWriting] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cost, setCost] = useState(0);
  const [openInstruction, setOpenInstruction] = useState<number | null>(null);
  const [instruction, setInstruction] = useState('');
  /*
   * A length for this redraft only, as typed. Kept as a string because an empty
   * box has to mean "leave it as planned" - a number state would need a
   * sentinel, and 0 is a length somebody could type.
   */
  const [length, setLength] = useState('');

  /** Stop after the current section when the writer asks. */
  const cancelled = useRef(false);

  const drafted = sections.filter((section) => section.body_md !== '');
  /* The words, not the markup: a coloured box is sixty characters nobody wrote.
     See shared/rich-format.ts. */
  const totalChars = drafted.reduce((sum, section) => sum + visibleLength(section.body_md), 0);
  const targetTotal = sections.reduce((sum, section) => sum + (section.target_chars ?? 0), 0);

  function apply(result: DraftResult) {
    setSections((current) =>
      current.map((section) =>
        section.ordinal === result.index
          ? { ...section, body_md: result.text, status: 'DRAFTED' }
          : section,
      ),
    );
    setCost((current) => current + result.microCost);
  }

  /** Keep an edit in view. The saving is the section editor's own business. */
  function hold(ordinal: number, bodyMd: string) {
    setSections((current) =>
      current.map((section) =>
        section.ordinal === ordinal ? { ...section, body_md: bodyMd, status: 'EDITED' } : section,
      ),
    );
  }

  function describe(err: unknown): string {
    if (!(err instanceof ApiError)) return '生成できませんでした';
    /*
     * Before the bare 409 below, which would otherwise claim a missing persona.
     * Both are 409 - this one means nobody has saved the instruction this
     * generation runs on, and the fix is a JMC screen rather than this one.
     */
    if (err.code === 'prompt_not_configured')
      return 'この生成に使う指示文がまだ保存されていません。JMCにプロンプトの設定を依頼してください。';
    if (err.status === 409) return '本文の生成にはペルソナが必要です。前の画面で選んでください。';
    if (err.code === 'quota_exceeded') return '今月の生成上限に達しています。';
    if (err.code === 'rate_limited')
      return 'AIサービスが混み合っています。しばらく待って再試行してください。';
    return err.fields[0] ?? err.message;
  }

  /** Write one section. Used both by the loop and by 書き直す. */
  async function draft(ordinal: number, note?: string, chars?: number): Promise<boolean> {
    setWriting(ordinal);
    setError(null);
    try {
      const result = await post<DraftResult>(
        `/articles/${article.id}/sections/${ordinal}/draft`,
        {
          ...(note?.trim() ? { instruction: note.trim() } : {}),
          // Absent means the planned target, which the server divides from the
          // article's total when the section has none of its own.
          ...(chars ? { targetChars: chars } : {}),
        },
      );
      apply(result);
      return true;
    } catch (err) {
      setError(describe(err));
      return false;
    } finally {
      setWriting(null);
    }
  }

  /**
   * Write every section that has no text yet, in order.
   *
   * In order and one at a time, because each section is given the end of the one
   * before it - running them at once would make every section the first section.
   */
  async function writeAll(redraw = false) {
    if (running) return;
    setRunning(true);
    cancelled.current = false;
    setError(null);
    try {
      for (const section of sections) {
        if (cancelled.current) break;
        if (!redraw && section.body_md !== '') continue;
        const ok = await draft(section.ordinal);
        // A refusal - quota, missing material - applies to every remaining
        // section too, so stopping is the honest response.
        if (!ok) break;
      }
    } finally {
      setRunning(false);
    }
  }

  /*
   * Arriving from the plan screen starts writing immediately - that is what
   * 保存して本文を作成 meant. A reload does not: the flag is in the navigation
   * state, so refreshing never quietly spends a second run.
   */
  const started = useRef(false);
  useEffect(() => {
    const wanted = (location.state as { write?: boolean } | null)?.write === true;
    if (!wanted || started.current) return;
    started.current = true;
    void writeAll();
    // Once, on mount: a generation is money.
  }, []);

  /** Compose the sections into the article, then hand over to the editor. */
  async function keep() {
    setSaving(true);
    setError(null);
    try {
      await post(`/articles/${article.id}/assemble`, { version: article.version });
      void navigate(`/articles/${article.id}`);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 409
            ? 'この記事は別の場所で更新されました。再読み込みしてください。'
            : (err.fields[0] ?? err.message)
          : '保存できませんでした',
      );
    } finally {
      setSaving(false);
    }
  }

  if (sections.length === 0) {
    return (
      <Card
        title={article.title}
        caption={`${article.store_name}・本文の作成`}
        actions={
          <Link className="btn btn--sm" to={`/articles/${article.id}/plan`}>
            構成を作る
          </Link>
        }
      >
        <Empty>
          構成がまだありません。見出しを決めてから本文を書きます。前の画面で構成を作成してください。
        </Empty>
      </Card>
    );
  }

  return (
    <>
      {/* Every step, in both directions: going back to a finished step is a
          normal thing to want, and nothing is lost by it. */}
      <StageBar articleId={article.id} current={'write'} />
      <Card
        title={article.title}
        caption={`${article.store_name}・本文の作成`}
        actions={
          <>
            {running ? (
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => {
                  cancelled.current = true;
                }}
              >
                この節で止める
              </button>
            ) : (
              <button
                className="btn btn--sm"
                type="button"
                disabled={writing !== null}
                onClick={() => void writeAll(drafted.length === sections.length)}
              >
                {drafted.length === 0
                  ? '本文を生成'
                  : drafted.length === sections.length
                    ? 'すべて書き直す'
                    : '残りを書く'}
              </button>
            )}
            <Link className="btn btn--ghost btn--sm" to={`/articles/${article.id}/plan`}>
              構成に戻る
            </Link>
          </>
        }
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="grid grid--3">
          <div className="stat">
            <div className="stat__label">進捗</div>
            <div className="stat__value stat__value--sm">
              {drafted.length} / {sections.length} 節
            </div>
            <div className="muted small">{running ? '執筆中…' : '節ごとに書けます'}</div>
          </div>
          <div className="stat">
            <div className="stat__label">文字数</div>
            <div className="stat__value stat__value--sm">{totalChars.toLocaleString('ja-JP')}</div>
            <div className="muted small">
              目安 {targetTotal ? targetTotal.toLocaleString('ja-JP') : '4,000〜5,000'}
            </div>
          </div>
          <div className="stat">
            <div className="stat__label">費用</div>
            <div className="stat__value stat__value--sm">
              {cost === 0 ? '—' : `$${(cost / 1_000_000).toFixed(4)}`}
            </div>
            <div className="muted small">今月の利用に加算</div>
          </div>
        </div>

        <div className="editpanel__actions">
          <button
            className="btn"
            type="button"
            disabled={saving || drafted.length === 0 || running}
            onClick={() => void keep()}
          >
            {saving ? '保存中…' : '本文として保存して編集へ'}
          </button>
          {drafted.length > 0 && drafted.length < sections.length && (
            <Pill tone="warn">未執筆の節は本文に入りません</Pill>
          )}
          {article.body_md !== '' && <Pill tone="warn">既存の本文は置き換えられます</Pill>}
        </div>
      </Card>

      {/*
        One card per section, in the order the article is read.

        Each one carries its own state, its own length against its own target and
        its own redraw - which is the whole point of writing section by section
        rather than in one shot.
      */}
      {sections.map((section) => {
        const chars = visibleLength(section.body_md);
        const target = section.target_chars;
        const off = target ? Math.abs(chars - target) / target : 0;
        const busy = writing === section.ordinal;

        return (
          <Card
            key={section.id}
            title={`${section.ordinal + 1}. ${section.heading}`}
            {...(section.intent ? { caption: section.intent } : {})}
            actions={
              <>
                {section.body_md !== '' && !busy && (
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    onClick={() =>
                      setOpenInstruction(
                        openInstruction === section.ordinal ? null : section.ordinal,
                      )
                    }
                  >
                    指示して書き直す
                  </button>
                )}
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  disabled={writing !== null || running}
                  onClick={() => void draft(section.ordinal)}
                >
                  {busy ? '執筆中…' : section.body_md === '' ? 'この節を書く' : '書き直す'}
                </button>
              </>
            }
          >
            {openInstruction === section.ordinal && (
              /*
               * The redraft request, as one panel rather than as loose fields.
               *
               * It sits between the card's heading and the draft it is about to
               * replace, so it has to read as a thing you are filling in and not
               * as part of the article. Two fields on one row, sized to what
               * they hold: an instruction is a sentence and takes the width, a
               * character count is three digits and does not.
               */
              <div className="subpanel">
                <p className="subpanel__title">この節を書き直す</p>
                <div className="grid grid--2">
                  <div className="field field--full" style={{ marginBottom: 0 }}>
                    <label htmlFor={`note-${section.ordinal}`}>指示（任意）</label>
                    <Textarea
                      id={`note-${section.ordinal}`}
                      minRows={2}
                      maxLength={300}
                      value={instruction}
                      placeholder="例：費用の内訳を表にして、所要時間も入れる"
                      onChange={(e) => setInstruction(e.target.value)}
                    />
                    <span className="field__hint">
                      文体・強調したい点・構成にだけ効きます。事実や規制の扱いは変わりません。
                    </span>
                  </div>
                  {/*
                    The length, at the button rather than two screens back. Empty
                    keeps the planned target - this section's share of the article
                    when nobody set one for it.
                  */}
                  <div className="field" style={{ marginBottom: 0, maxWidth: 200 }}>
                    <label htmlFor={`len-${section.ordinal}`}>文字数</label>
                    <input
                      id={`len-${section.ordinal}`}
                      type="number"
                      inputMode="numeric"
                      min={100}
                      max={8000}
                      step={50}
                      value={length}
                      placeholder={
                        section.target_chars ? String(section.target_chars) : '自動'
                      }
                      onChange={(e) => setLength(e.target.value)}
                    />
                    <span className="field__hint">空欄なら計画どおりの長さで書きます。</span>
                  </div>
                </div>
                <div className="editpanel__actions">
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={writing !== null}
                    onClick={() => {
                      const note = instruction;
                      const chars = Number(length);
                      setOpenInstruction(null);
                      setInstruction('');
                      setLength('');
                      void draft(
                        section.ordinal,
                        note,
                        Number.isFinite(chars) && chars >= 100 && chars <= 8000
                          ? chars
                          : undefined,
                      );
                    }}
                  >
                    この指示で書き直す
                  </button>
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    onClick={() => setOpenInstruction(null)}
                  >
                    やめる
                  </button>
                </div>
              </div>
            )}

            {section.body_md === '' ? (
              <Empty>
                {busy ? '執筆中です…' : 'まだ書かれていません。'}
              </Empty>
            ) : (
              <>
                {/*
                  The same editor as the article itself, here.

                  A section shown as preformatted text is something to accept or
                  regenerate, and regenerating to fix one price is a model call
                  for a typo. Fixed where it is read instead - and because the
                  edit is saved onto the section, assembling later carries it.
                */}
                <SectionEditor
                  key={section.id}
                  articleId={article.id}
                  ordinal={section.ordinal}
                  value={section.body_md}
                  onChange={(bodyMd) => hold(section.ordinal, bodyMd)}
                />
                <p className="muted small" style={{ marginBottom: 0 }}>
                  {chars.toLocaleString('ja-JP')}文字
                  {target && `／目安 ${target.toLocaleString('ja-JP')}文字`}
                  {/*
                    Said plainly rather than shown as a colour alone: a section
                    at half its target is the defect this whole change exists to
                    remove, and it should be visible without counting.
                  */}
                  {target && off > LENGTH_TOLERANCE && (
                    <>
                      {'　'}
                      <Pill tone="warn">{chars < target ? '短い' : '長い'}</Pill>
                    </>
                  )}
                </p>
              </>
            )}
          </Card>
        );
      })}

      {context.missing.length > 0 && (
        <Card title="不足している情報" caption="生成に使えなかったものです。">
          <ul className="feed">
            {context.missing.map((item) => (
              <li key={item}>
                <span className="feed__dot" style={{ background: 'var(--warn)' }} />
                {item}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
